// Parameter-change-vs-last-run diff (CR-20260831-004).
//
// Pure functions comparing the LIVE questionnaire state against the baseline the current Run
// captured (Run.sentInputs + the orchestrator's override snapshot). The result is a flat map of
// CHANGED-FIELD KEYS driving the yellow "this no longer matches the shown results" cues: input
// backgrounds, selected toggle segments, breadcrumb chips, and (via the stale union) the
// collapsed-launcher FAB.
//
// Key grammar (every key is a plain string, values are always `true`):
//   date.kind / date.value                      — the tab's date selection
//   <side>.priceRange.kind                      — the Price Range mode toggle
//   <side>.startStrike / <side>.endStrike       — strike pickers (all strike-carrying modes)
//   <side>.atm.anchor / .atm.offset / .atm.subKind / .atm.useExpectedRange / .atm.extent
//   <side>.minProfit.mode / .minProfit.amount
//   <side>.maxRisk.kind / .maxRisk.amount / .maxRisk.normalized / .maxRisk.level
//   ovr.<dateKey>.ivCall / .ivPut / .hsCall / .hsPut / .vsCall / .vsPut   — override boxes, per side
// where <side> is 'calls.' / 'puts.' on the dual tab and 'side.' on the single-side tabs.
//
// Union semantics (the ruling shape, CR plan §diff): when a discriminated union's KIND differs,
// ONLY the kind key is flagged — the newly selected toggle paints yellow ("expected range
// highlighted yellow rather than blue", owner video 2026-08-31) — and the inner values are NOT
// compared (they have no baseline of the same shape to compare against). Leaf values are compared
// only while the kinds match. A maxRisk that is null on either end (follow-through-only coverage)
// is likewise judged by its causing toggle, never by phantom leaf diffs.
//
// NaN discipline: unset numeric inputs live in the model as NaN (the orNaN idiom). NaN-vs-NaN is
// EQUAL here — both mean "empty box" — so clearing a box back to the empty state it was sent in
// does not flag.
//
// ES5 note: arrays + indexOf/for-in only (no Set/Map iteration, no for..of), matching the family.

import {
    isDualSideInputs,
    MarketConditionOverride,
    MarketConditionOverrides,
    OpgDateSelection,
    OpgMaxRisk,
    OpgPriceRange,
    OVERRIDE_VALUE_KEYS,
    SideInputs,
    SingleSideTabInputs,
    TabInputs
} from './opg-copilot-domain';

// Flat changed-key map. Present key => changed. Always safe to hand to OnPush children —
// producers build a FRESH object per recompute (reference change is the wake-up signal).
export type ParamChangeMap = { [key: string]: boolean };

export function hasAnyChange(map: ParamChangeMap | null): boolean {
    if (!map) {
        return false;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k)) {
            return true;
        }
    }
    return false;
}

// The keys under `prefix`, re-keyed without it — what a side questionnaire receives
// ('calls.minProfit.amount' -> 'minProfit.amount'). Fresh object every call, by design.
export function subMap(map: ParamChangeMap | null, prefix: string): ParamChangeMap {
    const out: ParamChangeMap = {};
    if (!map) {
        return out;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k) && k.length > prefix.length && k.indexOf(prefix) === 0) {
            out[k.substring(prefix.length)] = true;
        }
    }
    return out;
}

// Does any key start with one of the prefixes (breadcrumb chip aggregation)?
export function anyKeyWithPrefix(map: ParamChangeMap | null, prefixes: string[]): boolean {
    if (!map) {
        return false;
    }
    for (const k in map) {
        if (!map.hasOwnProperty(k)) {
            continue;
        }
        for (let i = 0; i < prefixes.length; i++) {
            if (k.indexOf(prefixes[i]) === 0) {
                return true;
            }
        }
    }
    return false;
}

// ---- questionnaire inputs -----------------------------------------------------------------

// Live tab inputs vs the run's sentInputs snapshot. No baseline (never run / conversation
// reset) => EMPTY map: nothing can be "changed since the run" before a run exists.
export function diffTabInputs(baseline: TabInputs | null, live: TabInputs | null): ParamChangeMap {
    const out: ParamChangeMap = {};
    if (!baseline || !live) {
        return out;
    }
    diffDate(baseline.date, live.date, out);
    if (isDualSideInputs(baseline) && isDualSideInputs(live)) {
        diffSide(baseline.calls, live.calls, 'calls.', out);
        diffSide(baseline.puts, live.puts, 'puts.', out);
    } else if (!isDualSideInputs(baseline) && !isDualSideInputs(live)) {
        diffSide((baseline as SingleSideTabInputs).side, (live as SingleSideTabInputs).side, 'side.', out);
    }
    return out;
}

function diffDate(base: OpgDateSelection, live: OpgDateSelection | null, out: ParamChangeMap): void {
    if (!base) {
        return;
    }
    if (!live) {
        // The user cleared a picker after the run — the sent date no longer stands.
        out['date.value'] = true;
        return;
    }
    if (base.kind !== live.kind) {
        out['date.kind'] = true;
        return;
    }
    if (base.kind === 'single' && live.kind === 'single') {
        if (!sameDate(base.date, live.date)) {
            out['date.value'] = true;
        }
    } else if (base.kind === 'range' && live.kind === 'range') {
        if (!sameDate(base.start, live.start) || !sameDate(base.end, live.end)) {
            out['date.value'] = true;
        }
    }
    // 'loaded' carries no fields — same kind means same selection.
}

function diffSide(base: SideInputs, live: SideInputs, prefix: string, out: ParamChangeMap): void {
    if (base && live) {
        if ((base.mode || 'custom') !== (live.mode || 'custom')) { out[prefix + 'mode'] = true; return; }
        if (live.mode === 'preset') {
            if (base.presetId !== live.presetId) { out[prefix + 'presetId'] = true; }
            if (base.presetAnchor !== live.presetAnchor) { out[prefix + 'presetAnchor'] = true; }
            return;
        }
    }
    if (!base || !live) {
        return;
    }
    diffPriceRange(base.priceRange, live.priceRange, prefix, out);
    if (base.minProfit && live.minProfit) {
        if (!!base.minProfit.normalized !== !!live.minProfit.normalized) {
            out[prefix + 'minProfit.mode'] = true;
        }
        if (!numEq(base.minProfit.amount, live.minProfit.amount)) {
            out[prefix + 'minProfit.amount'] = true;
        }
    }
    diffMaxRisk(base.maxRisk, live.maxRisk, prefix, out);
}

function diffPriceRange(base: OpgPriceRange, live: OpgPriceRange, prefix: string, out: ParamChangeMap): void {
    if (!base || !live) {
        return;
    }
    if (base.kind !== live.kind) {
        out[prefix + 'priceRange.kind'] = true;
        return;
    }
    if (base.kind === 'strikes' && live.kind === 'strikes') {
        if (!numEq(base.startStrike, live.startStrike)) {
            out[prefix + 'startStrike'] = true;
        }
        if (!numEq(base.endStrike, live.endStrike)) {
            out[prefix + 'endStrike'] = true;
        }
    } else if ((base.kind === 'followThrough' && live.kind === 'followThrough')
        || (base.kind === 'expectedRange' && live.kind === 'expectedRange')) {
        if (!numEq(base.startStrike, live.startStrike)) {
            out[prefix + 'startStrike'] = true;
        }
    } else if (base.kind === 'atmOffset' && live.kind === 'atmOffset') {
        if (base.anchor !== live.anchor) {
            out[prefix + 'atm.anchor'] = true;
        }
        if (!numEq(base.offset, live.offset)) {
            out[prefix + 'atm.offset'] = true;
        }
        const bs = base.sub;
        const ls = live.sub;
        if (!bs || !ls) {
            return;
        }
        if (bs.kind !== ls.kind) {
            out[prefix + 'atm.subKind'] = true;
            return;
        }
        if ((bs.kind === 'range' && ls.kind === 'range') || (bs.kind === 'both' && ls.kind === 'both')) {
            const bv = bs.value;
            const lv = ls.value;
            if (!bv || !lv) {
                return;
            }
            if (bv.mode !== lv.mode) {
                // The static-vs-dynamic sizing flip IS the "Use Expected Range" checkbox.
                out[prefix + 'atm.useExpectedRange'] = true;
            } else if (bv.mode === 'dollar' && lv.mode === 'dollar' && !numEq(bv.extent, lv.extent)) {
                out[prefix + 'atm.extent'] = true;
            }
        }
    }
}

function diffMaxRisk(base: OpgMaxRisk | null, live: OpgMaxRisk | null, prefix: string, out: ParamChangeMap): void {
    // Either end null = follow-through-only coverage on that end; the coverage toggle that caused
    // it is already flagged by diffPriceRange, and there is no like-for-like value to compare.
    if (!base || !live) {
        return;
    }
    if (base.kind !== live.kind) {
        out[prefix + 'maxRisk.kind'] = true;
        return;
    }
    if (base.kind === 'dollar' && live.kind === 'dollar') {
        if (!numEq(base.amount, live.amount)) {
            out[prefix + 'maxRisk.amount'] = true;
        }
        if (!!base.normalized !== !!live.normalized) {
            out[prefix + 'maxRisk.normalized'] = true;
        }
    } else if (base.kind === 'relative' && live.kind === 'relative') {
        if (base.level !== live.level) {
            out[prefix + 'maxRisk.level'] = true;
        }
    }
}

// ---- market-condition overrides -----------------------------------------------------------

// The USER-SUPPLIED override records only (both maps hold edited records exclusively — that is
// the overridesByTab invariant). Live market readings are deliberately NOT part of this diff:
// they tick on their own and are not a user parameter change. A record present on one side and
// absent on the other flags every value the present record supplies.
export function diffOverrides(baseline: MarketConditionOverrides | null,
                              live: MarketConditionOverrides | null): ParamChangeMap {
    const out: ParamChangeMap = {};
    const keys: string[] = [];
    collectKeys(baseline, keys);
    collectKeys(live, keys);
    for (let i = 0; i < keys.length; i++) {
        const dateKey = keys[i];
        const b = baseline ? baseline[dateKey] : null;
        const l = live ? live[dateKey] : null;
        diffOverrideRecord(b || null, l || null, 'ovr.' + dateKey + '.', out);
    }
    return out;
}

function collectKeys(map: MarketConditionOverrides | null, into: string[]): void {
    if (!map) {
        return;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k) && into.indexOf(k) < 0) {
            into.push(k);
        }
    }
}

function diffOverrideRecord(base: MarketConditionOverride | null, live: MarketConditionOverride | null,
                            prefix: string, out: ParamChangeMap): void {
    if (!base && !live) {
        return;
    }
    // CR-20260921-002: one key per side per value — the fieldset paints its own side's boxes.
    for (let i = 0; i < OVERRIDE_VALUE_KEYS.length; i++) {
        const field = OVERRIDE_VALUE_KEYS[i];
        if (!nullNumEq(base ? base[field] : null, live ? live[field] : null)) {
            out[prefix + field] = true;
        }
    }
}

// ---- primitives ---------------------------------------------------------------------------

// Domain numerics: NaN means "empty box" (orNaN), so NaN equals NaN here.
function numEq(a: number, b: number): boolean {
    const aNan = typeof a !== 'number' || isNaN(a);
    const bNan = typeof b !== 'number' || isNaN(b);
    if (aNan || bNan) {
        return aNan === bNan;
    }
    return a === b;
}

// Override values: null means "not supplied" (distinct home from NaN, same meaning here).
function nullNumEq(a: number | null, b: number | null): boolean {
    const aEmpty = a == null || (typeof a === 'number' && isNaN(a));
    const bEmpty = b == null || (typeof b === 'number' && isNaN(b));
    if (aEmpty || bEmpty) {
        return aEmpty === bEmpty;
    }
    return a === b;
}

function sameDate(x: Date, y: Date): boolean {
    const xv = x instanceof Date && !isNaN(x.getTime());
    const yv = y instanceof Date && !isNaN(y.getTime());
    if (!xv || !yv) {
        return xv === yv;
    }
    return x.getTime() === y.getTime();
}
